/**
 * How far the server's clock is ahead of this machine's, in ms: `Date.now() +
 * offset` is the server's time. Timed moments the server sets for everyone,
 * such as the Hide and Seek Teams coin landing, use it to happen together on
 * every client however far its own clock is off.
 *
 * Measured with TIME_SYNC: a few round trips, keeping the quickest (the least
 * room for network delay to skew it), taking the server's time as halfway
 * through the trip. Measured once per connection, shared by every caller, and
 * again after a reconnect. 0 until measured, or if the server can't answer.
 */
import { useEffect, useState } from 'react';
import { SocketEvent } from '@swtorbit/shared-socket-events';
import { useSocketContext } from './SocketProvider';
const SAMPLES = 3;
const TIMEOUT_MS = 5000;
function sampleOnce(socket) {
    return new Promise((resolve, reject) => {
        const sentAt = Date.now();
        socket.timeout(TIMEOUT_MS).emit(SocketEvent.TIME_SYNC, {}, (error, response) => {
            if (error || typeof response?.serverNow !== 'number') {
                reject(error ?? new Error('No server time'));
                return;
            }
            const roundTripMs = Date.now() - sentAt;
            resolve({ offsetMs: response.serverNow - (sentAt + roundTripMs / 2), roundTripMs });
        });
    });
}
/** The offset from the quickest of a few round trips. */
export async function measureServerClockOffset(socket, samples = SAMPLES) {
    let best = null;
    for (let i = 0; i < samples; i++) {
        const sample = await sampleOnce(socket);
        if (!best || sample.roundTripMs < best.roundTripMs)
            best = sample;
    }
    return Math.round(best.offsetMs);
}
// One measurement per connection, whoever asks first.
let measured = null;
export function useServerClockOffset() {
    const { socket, isConnected } = useSocketContext();
    const [offsetMs, setOffsetMs] = useState(0);
    useEffect(() => {
        const socketId = socket?.id;
        if (!socket || !isConnected || !socketId)
            return;
        if (measured?.socketId !== socketId) {
            const offset = measureServerClockOffset(socket);
            measured = { socketId, offset };
            // A failed measurement is tried again by the next caller.
            offset.catch(() => {
                if (measured?.offset === offset)
                    measured = null;
            });
        }
        let cancelled = false;
        measured.offset.then((offset) => {
            if (!cancelled)
                setOffsetMs(offset);
        }).catch(() => {
            // The server's clock is unknown: assume this one is right.
        });
        return () => {
            cancelled = true;
        };
    }, [socket, isConnected]);
    return offsetMs;
}
